import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ProductComparatorService } from './core/product-comparator.service';
import { ActivatedRoute } from '@angular/router';
import {
  PComparatorProductsRequest,
  PRODUCT_COMPARATOR_TYPE,
  PComparatorStatusRequest,
} from './core/product-comparator.model';
import { failStatus, initialStatus, loadingStatus, successStatus } from '../../../../core/models/request-status.model';
import { Option } from '../../../../core/models/option.model';
import { ProductComparatorFloatingService } from '../product-comparator-floating/core/product-comparator-floating.service';
import { ToolsService } from '../../../../core/services/tools.service';
import { DEVICE_SIZE } from '../../../../core/models/others.model';
import { Product, RoutingService } from '@spartacus/core';
import { ProductAuxService } from '../_core/product-aux.service';

@Component({
  selector: 'product-comparator',
  templateUrl: './product-comparator.component.html',
  styleUrl: './product-comparator.component.scss',
  standalone: false,
})
export class ProductComparatorComponent implements OnInit, AfterViewInit, OnDestroy {
  PRODUCT_COMPARATOR_TYPE = PRODUCT_COMPARATOR_TYPE;

  @ViewChild('stickyElement') stickyElement!: ElementRef;
  @ViewChild('container') container?: ElementRef;
  @ViewChild('checkbox') highlightInput?: ElementRef;

  pComparatorType?: PRODUCT_COMPARATOR_TYPE;
  subscription = new Subscription();
  reqPComparator$ = new BehaviorSubject<PComparatorStatusRequest>(initialStatus);
  pageType = this.productComparatorService.getCurrentComparationType();

  viewType: Option = { code: 'allSpecs', label: 'All Specs' };
  viewOptions: Option[] = [
    { code: 'allSpecs', label: 'All Specs' },
    { code: 'keySpecs', label: 'Key Specs' },
  ];

  constructor(
    private pfloattingCompService: ProductComparatorFloatingService,
    private productComparatorService: ProductComparatorService,
    private activatedRoute: ActivatedRoute,
    private toolsService: ToolsService,
    private routingService: RoutingService,
    private productAuxService: ProductAuxService,
  ) {}

  ngOnInit(): void {
    this.populateProductComparator();
  }

  ngAfterViewInit(): void {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && entry.boundingClientRect.top <= 0) {
          this.stickyElement.nativeElement.classList.add('collapsed');
        } else {
          this.stickyElement.nativeElement.classList.remove('collapsed');
        }
      },
      { threshold: [1.0], rootMargin: '-1px 0px 0px 0px' },
    );

    observer.observe(this.stickyElement.nativeElement);
    this.checkHightlightView();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  checkHightlightView(): void {
    const input = this.highlightInput?.nativeElement as HTMLInputElement;
    input.checked = true;
    this.highlightView(input);
  }

  syncScroll(source: HTMLElement, target0: HTMLElement, target1: HTMLElement): void {
    target0.scrollLeft = source.scrollLeft;
    target1.scrollLeft = source.scrollLeft;
  }

  getClassContainer(qty: number): string {
    if (qty <= 4) {
      return 'qty-' + qty;
    }

    return 'many-items';
  }

  resizeColumns(qtyItems: number): void {
    const $container = this.container?.nativeElement;
    let $cols = $container.querySelectorAll('.column-x');

    const containerWidth = $container.offsetWidth - 24;
    let colWidth = containerWidth / (qtyItems + 1);

    colWidth = colWidth < 336 ? 336 : colWidth;

    $cols.forEach((col: HTMLElement) => {
      col.style.width = `${colWidth}px`;
    });

    // Section Name

    $cols = $container.querySelectorAll('.row-x.section-name .column-x');

    $cols.forEach((col: HTMLElement) => {
      col.style.width = `${containerWidth}px`;
    });
  }

  populateProductComparator(): void {
    this.pComparatorType = this.productComparatorService.getCurrentComparationType();

    this.subscription.add(
      this.activatedRoute.queryParams.subscribe(params => {
        const pids = params['pids'];
        const pid = params['pid'];
        const useProductReferences = params['useProductReferences'];

        if (pids) {
          this.getPComparator({ pids });
          return;
        }

        this.getPComparator({ pid, useProductReferences });
      }),
    );
  }

  getPComparator(config: PComparatorProductsRequest) {
    this.reqPComparator$.next(loadingStatus);

    this.subscription.add(
      this.productComparatorService.getPComparator(config).subscribe(productRequest => {
        const currentSize = this.toolsService.getCurrentSize();
        const qtyProduct = productRequest.pComparator?.products?.length ?? 0;

        productRequest.pComparator?.products?.forEach(product => {
          product.thirdPartyPriceStatus = initialStatus;
          product.yourPrice = undefined;
          product.differencePrice = undefined;
        });

        this.reqPComparator$.next(productRequest);

        if (this.pageType === PRODUCT_COMPARATOR_TYPE.FLOATTING && qtyProduct > 0) {
          this.pfloattingCompService.removeAllProducts();

          (productRequest?.pComparator?.products || []).forEach(product => {
            this.pfloattingCompService.addProduct(product);
          });
        }

        if (qtyProduct < 2) {
          this.routingService.go({ cxRoute: 'home' });
          return;
        }

        if (currentSize === DEVICE_SIZE.DESKTOP || currentSize === DEVICE_SIZE.WIDESCREEN) {
          setTimeout(() => {
            this.resizeColumns(qtyProduct);
          }, 50);
        }

        this.gerPrices(productRequest.pComparator?.products || []);
      }),
    );
  }

  gerPrices(products: Product[]) {
    const productsIDS = products.map(product => product.code) as string[];

    this.subscription.add(
      this.productAuxService.getProductsPrices({ productsIDS }).subscribe(productPrices => {
        products.forEach(product => {
          const productPrice = productPrices.products?.find(productPrice => productPrice.productCode === product.code);

          if (productPrice) {
            product.thirdPartyPriceStatus = successStatus;
            product.yourPrice = productPrice.yourPrice;
            product.differencePrice = productPrice.difference;
          } else {
            product.thirdPartyPriceStatus = failStatus;
          }
        });

        const currentValue = this.reqPComparator$.getValue();

        if (currentValue.pComparator) {
          currentValue.pComparator.products = products;
        }

        this.reqPComparator$.next(currentValue);
      }),
    );
  }

  onlyKeySpecsView(option: Option) {
    const $container = this.container?.nativeElement;

    if (option.code === 'keySpecs') {
      $container.classList.add('key-spec-view');
    } else {
      $container.classList.remove('key-spec-view');
    }
  }

  highlightView(input: HTMLInputElement) {
    const $container = this.container?.nativeElement;

    if (input.checked) {
      $container.classList.add('highlight-view');
    } else {
      $container.classList.remove('highlight-view');
    }
  }

  toggleRow(button: HTMLButtonElement, sectionID: string): void {
    const rows = this.container?.nativeElement.querySelectorAll(`.js-row-${sectionID}`);

    button.classList.toggle('active');

    rows.forEach((row: HTMLElement) => {
      row.classList.toggle('d-none');
    });
  }
	
}